import { useEffect, useMemo, useRef, useState } from "react";
import { track } from "../../analytics";
import type { FormEvent } from "react";
import { createPortal } from "react-dom";
import type { Gender, SecurityAnswer } from "@gooner/shared";
import {
  PASSWORD_MIN,
  SECURITY_ANSWER_COUNT,
  SECURITY_QUESTIONS,
  USERNAME_MAX,
  dobError,
  emailError,
  passwordError,
  securityAnswerError,
  usernameError,
} from "@gooner/shared";
import { publicSettings, settingsUnread } from "../../lib/publicSettings";
import { useSession } from "../../lib/session";
import { userApi } from "../../lib/api";
import { Button, Field, TextInput, cx } from "../ui/primitives";
import { IconClose } from "../ui/icons";
import { captureFocusReturn } from "../../lib/focusReturn";
import { Gooncha } from "./Gooncha";
import {
  DobField,
  EMAIL_NEVER_NOTE,
  FieldNote,
  GenderField,
  SecurityQuestionRow,
} from "./accountFields";

/** `recover` (2026-10-25) is "Forgot your password?": 1 names the account behind the
 *  robot check, 2 answers its three questions and sets a new password. */
type Mode = "login" | "register" | "recover";
/** Register: 1 is who you are, 2 is how you get back in. Recover: see Mode. */
type Step = 1 | 2;

/** Fails closed to 18 — see the settings read below, and AgeGate's precedent. */
const DEFAULT_MIN_AGE = 18;

const EMPTY3 = ["", "", ""] as const;

/**
 * Sign-in / sign-up modal. Anonymous users open it from the account button, or from a
 * gated action (fap, add-to-collection) — in which case `reason` explains why they're
 * being asked.
 *
 * Sign-up is TWO stages (2026-09-20). Stage 1 is the account — email, name, password
 * twice, birthday, gender. Stage 2 is the three security questions and the robot check.
 * Seven fields on one card reads as a wall, and a wall is where people leave.
 *
 * The EMAIL LEADS, and the username is proposed from its local part, because one of
 * those is something people already know and the other is something they have to invent
 * — and inventing it is the step that stalls a sign-up. Birthday and gender share a row:
 * two short answers do not each deserve the full width.
 *
 * Sign-IN is untouched: same two fields, same checkbox, same three clauses on the submit.
 */
export function AuthModal({
  onClose,
  initialMode = "login",
  reason,
}: {
  onClose: () => void;
  initialMode?: Exclude<Mode, "recover">;
  reason?: string;
}) {
  const login = useSession((s) => s.login);
  const register = useSession((s) => s.register);
  const recover = useSession((s) => s.recover);

  const [mode, setMode] = useState<Mode>(initialMode);
  const [step, setStep] = useState<Step>(1);
  const [username, setUsername] = useState("");
  /** Has the name been typed in by hand? Once it has, the email stops driving it. */
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [email, setEmail] = useState("");
  const [dob, setDob] = useState("");
  const [gender, setGender] = useState<Gender | "">("");
  const [questions, setQuestions] = useState<string[]>([...EMPTY3]);
  const [answers, setAnswers] = useState<string[]>([...EMPTY3]);
  /** Recovery: the ticket step 1 handed out, and the account's questions, in order. */
  const [recoverTicket, setRecoverTicket] = useState<string | null>(null);
  const [recoverQuestionIds, setRecoverQuestionIds] = useState<string[]>([]);
  const [recoverAnswers, setRecoverAnswers] = useState<string[]>([...EMPTY3]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /*
   * GOONCHA. `enabled` starts null (unknown) and FAILS CLOSED — if /settings cannot be
   * read, the checkbox shows anyway (AgeGate's precedent): the server would demand a
   * token we don't have, and a lockout with no checkbox is strictly worse than a
   * checkbox nobody needed. The read goes through the page-wide publicSettings cache,
   * whose fallback is DEFAULT_SETTINGS (gooncha OFF), so the unread case is asked for
   * by name rather than taken from the default. The token survives the login↔register
   * toggle on purpose — one solve covers either door — and is cleared when a submit
   * burns it (the server consumes the one-shot token before it checks the password).
   */
  const [goonchaEnabled, setGoonchaEnabled] = useState<boolean | null>(null);
  const [goonchaToken, setGoonchaToken] = useState<string | null>(null);
  const [goonchaOpen, setGoonchaOpen] = useState(false);
  /** The age gate's own number, so "how old must you be" lives in ONE place. Fails
   *  closed to 18 on an unreadable settings read, exactly like the checkbox above. */
  const [minAge, setMinAge] = useState(DEFAULT_MIN_AGE);

  // Top of the registration funnel: which door, and what gated action (if any) opened it.
  // Once per open — the mode toggle below is a step inside the same visit, not a new one.
  useEffect(() => {
    track("auth_opened", { mode: initialMode, reason: reason ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    void publicSettings().then((s) => {
      if (cancelled) return;
      setGoonchaEnabled(settingsUnread(s) || s.gooncha.enabled);
      setMinAge(s.ageGate.minAge);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const registering = mode === "register";
  const recovering = mode === "recover";
  /** Either wizard's second card: the stage-1 fields fold away on both. */
  const onStage2 = (registering || recovering) && step === 2;
  /** Hoisted, but spelled exactly as it was — tools/checks/gooncha.mts pins this. */
  const needsGooncha = goonchaEnabled === true && !goonchaToken;

  /**
   * The email leads and the username follows it, because one of them is a thing people
   * already know and the other is a thing they have to invent. Typing in the name field
   * ends the arrangement for good (`usernameTouched`) — an autofill that overwrites what
   * somebody deliberately typed is worse than no autofill.
   */
  const onEmailChange = (value: string) => {
    setEmail(value);
    if (usernameTouched) return;
    // Only what a username may contain — usernameError owns the rule, this just avoids
    // proposing a name it would immediately refuse.
    const local = (value.split("@")[0] ?? "").replace(/[^a-zA-Z0-9_]/g, "").slice(0, USERNAME_MAX);
    setUsername(local);
  };

  /** Stage 1's rules, in the order a person fills the form. */
  const stage1Error = (): string | null =>
    usernameError(username.trim()) ??
    emailError(email) ??
    passwordError(password, confirm) ??
    dobError(dob, Date.now(), minAge) ??
    (gender ? null : "Pick a gender.");

  const stage2Error = (): string | null => {
    if (questions.some((q) => !q)) return `Pick ${SECURITY_ANSWER_COUNT} questions.`;
    if (new Set(questions).size !== SECURITY_ANSWER_COUNT) return "Pick three different questions.";
    for (const a of answers) {
      const err = securityAnswerError(a);
      if (err) return err;
    }
    return null;
  };

  /* Emptiness disables the button; INVALIDITY speaks. That is the existing contract
   * (the old one tested `!username || !password`, never usernameError), and a dead
   * button that never says why is worse than a press that explains itself. */
  const stage1Filled = !!username && !!password && !!confirm && !!email && !!dob && !!gender;
  const stage2Filled = questions.every(Boolean) && answers.every((a) => a.trim());

  const setAt = (list: string[], i: number, v: string) =>
    list.map((old, idx) => (idx === i ? v : old));

  const confirmTone = useMemo<"ok" | "bad" | undefined>(() => {
    if (!confirm) return undefined;
    return password === confirm ? "ok" : "bad";
  }, [password, confirm]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setError(null);

    if (registering && step === 1) {
      const err = stage1Error();
      if (err) return setError(err);
      setStep(2);
      // The missing middle of the funnel. Without it, a wizard that loses half its
      // sign-ups between the screens is indistinguishable from one that never did.
      track("signup_stage_two");
      return;
    }

    if (recovering) {
      if (step === 2) {
        if (recoverAnswers.some((a) => !a.trim())) return setError("Answer all three questions.");
        const pwErr = passwordError(password, confirm);
        if (pwErr) return setError(pwErr);
      }
      setBusy(true);
      try {
        if (step === 1) {
          const r = await userApi.recoverStart({
            username: username.trim(),
            ...(goonchaToken ? { gooncha: goonchaToken } : {}),
          });
          setRecoverTicket(r.ticket);
          setRecoverQuestionIds(r.questionIds);
          setRecoverAnswers([...EMPTY3]);
          setPassword("");
          setConfirm("");
          setStep(2);
          // The pass was spent naming the account; step 2 carries the ticket instead.
          if (goonchaEnabled) setGoonchaToken(null);
        } else {
          await recover({ ticket: recoverTicket ?? "", answers: recoverAnswers, password, passwordConfirm: confirm });
          onClose();
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "";
        const status = Number(/^(\d+):/.exec(message)?.[1] ?? 0);
        track("auth_failed", { mode, status });
        setError(err instanceof Error ? message.replace(/^\d+:\s*/, "") : "Something went wrong.");
        if (step === 1 && goonchaEnabled) setGoonchaToken(null);
        // An expired ticket, or the day's last try: back to naming the account.
        if (step === 2 && (status === 410 || status === 429)) {
          setStep(1);
          setRecoverTicket(null);
        }
      } finally {
        setBusy(false);
      }
      return;
    }

    if (registering) {
      // Re-checked because the mode toggle lets stage-1 fields be edited from here;
      // bouncing back with the message beats a server 400 the form could have caught.
      const s1 = stage1Error();
      if (s1) {
        setStep(1);
        return setError(s1);
      }
      const s2 = stage2Error();
      if (s2) return setError(s2);
    }

    setBusy(true);
    try {
      if (mode === "login") await login(username.trim(), password, goonchaToken ?? undefined);
      else
        await register({
          username: username.trim(),
          password,
          passwordConfirm: confirm,
          email: email.trim(),
          dob,
          gender: gender as Gender,
          security: questions.map(
            (questionId, i): SecurityAnswer => ({ questionId, answer: answers[i]! }),
          ),
          ...(goonchaToken ? { gooncha: goonchaToken } : {}),
        });
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "";
      // The status code only (401 wrong password, 409 name taken, 0 network) — never the
      // text, which can echo what the user typed.
      track("auth_failed", { mode, status: Number(/^(\d+):/.exec(message)?.[1] ?? 0) });
      setError(err instanceof Error ? err.message.replace(/^\d+:\s*/, "") : "Something went wrong.");
      // The server consumed the one-shot pass before it looked at the password, so a
      // failed submit means the box has to be ticked again.
      if (goonchaEnabled) setGoonchaToken(null);
    } finally {
      setBusy(false);
    }
  }

  const submitLabel = busy
    ? "…"
    : recovering
      ? step === 1
        ? "Next"
        : "Reset password"
      : mode === "login"
      ? "Sign in"
      : step === 1
        ? "Next"
        : "Create account";

  const submitDisabled =
    busy ||
    (recovering
      ? step === 1
        ? !username.trim() || needsGooncha
        : recoverAnswers.some((a) => !a.trim()) || !password || !confirm
      : mode === "login"
      ? !username || !password || needsGooncha
      : step === 1
        ? !stage1Filled
        : !stage2Filled || needsGooncha);

  // 2026-10-09 (Casey): Esc closes the modal like ✕ does.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /*
   * Focus goes back to whatever opened this when it closes (QA 2026-10-21: Esc dropped it
   * on <body>, so the next Tab restarted at "Skip to content"); a ☰ row that has since
   * unmounted hands it to the ☰. Captured at FIRST RENDER, not in an effect: the field's
   * autoFocus has already taken focus by the time effects run. Restored only once the
   * form has really left the document — StrictMode's rehearsal unmount leaves it in place
   * and must not pull focus out of the field.
   */
  const [restoreFocus] = useState(captureFocusReturn);
  const formRef = useRef<HTMLFormElement>(null);
  useEffect(() => {
    const form = formRef.current;
    return () => {
      if (!form?.isConnected) restoreFocus();
    };
  }, [restoreFocus]);

  // Portal to <body> so the overlay centers on the viewport instead of being
  // trapped inside the backdrop-blurred nav header (a fixed-position containing block).
  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <form
        ref={formRef}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className={cx(
          "w-full rounded-2xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-6 shadow-2xl",
          // Wider while the grid is up: nine loops at ~109px are squint material;
          // ~130px tiles are a judgment. Back to the compact card for the form.
          goonchaOpen ? "max-w-md" : "max-w-sm",
          // This card used to promise it never scrolled, and stage 2 broke that promise:
          // a header, three labelled selects, three answers, the robot row and two
          // buttons do not fit a 667px phone. It scrolls now rather than overflowing off
          // the bottom where nobody finds the button. GOONCHA still folds the form away
          // when it opens — that is a separate rule, and still the better one.
          "max-h-[calc(100dvh-2rem)] overflow-y-auto",
        )}
      >
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-zinc-100">
            {recovering ? "Reset password" : mode === "login" ? "Sign in" : "Create account"}
          </h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded p-1 text-zinc-400 hover:bg-[var(--color-panel-2)] hover:text-white"
            >
              <IconClose />
            </button>
          </div>
        </div>

        {/* Why they were asked to sign in — beside the point once they're resetting. */}
        {reason && !recovering ? (
          <p className="mb-3 rounded-lg border border-[var(--color-accent)]/30 bg-[var(--color-accent)]/10 px-3 py-2 text-sm text-[var(--color-accent-soft)]">
            {reason}
          </p>
        ) : null}

        {/* ---- stage 1 / sign-in ---- */}
        <div className={cx("space-y-3", (goonchaOpen || onStage2) && "hidden")}>
          {registering ? (
            <div>
              <Field label="Email">
                <TextInput
                  autoFocus
                  type="email"
                  value={email}
                  onChange={(e) => onEmailChange(e.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  placeholder="you@example.com"
                />
              </Field>
              <FieldNote>{EMAIL_NEVER_NOTE}</FieldNote>
            </div>
          ) : null}

          <Field label="Username" hint={registering ? "3–24 characters" : undefined}>
            <TextInput
              autoFocus={!registering}
              value={username}
              onChange={(e) => {
                // Any keystroke here ends the autofill — from then on the name is
                // theirs, and a later email edit must not quietly overwrite it.
                setUsernameTouched(true);
                setUsername(e.target.value);
              }}
              autoComplete="username"
              placeholder="gooner"
            />
          </Field>

          {recovering ? (
            <p className="text-xs leading-snug text-zinc-400">
              Enter your username. You'll answer the three security questions you picked
              when you signed up — all three have to match.
            </p>
          ) : null}

          <div className={cx(recovering && "hidden")}>
            <Field label="Password">
              <TextInput
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                placeholder="••••••••"
              />
            </Field>
            {registering ? (
              <FieldNote tone={password && password.length >= PASSWORD_MIN ? "ok" : undefined}>
                At least {PASSWORD_MIN} characters.
              </FieldNote>
            ) : null}
          </div>

          {registering ? (
            <>
              <div>
                <Field label="Confirm password">
                  <TextInput
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    autoComplete="new-password"
                    placeholder="••••••••"
                  />
                </Field>
                <FieldNote tone={confirmTone}>
                  {!confirm ? "" : password === confirm ? "Passwords match." : "Passwords don't match."}
                </FieldNote>
              </div>
              {/* Two short answers, one line — a full-width row each made the card
                  taller than the questions deserve. */}
              <div className="grid grid-cols-2 gap-3">
                <DobField value={dob} onChange={setDob} minAge={minAge} />
                <GenderField value={gender} onChange={setGender} />
              </div>
            </>
          ) : null}
        </div>

        {/* ---- stage 2: how you get back in ---- */}
        <div className={cx("space-y-2", (!onStage2 || !registering || goonchaOpen) && "hidden")}>
          <p className="text-xs leading-snug text-zinc-400">
            Pick three questions. If you ever lose your password, these are how you get back
            in — answers aren't case-sensitive.
          </p>
          {questions.map((q, i) => (
            <SecurityQuestionRow
              key={i}
              index={i}
              questionId={q}
              answer={answers[i] ?? ""}
              taken={questions}
              onQuestion={(id) => setQuestions((prev) => setAt(prev, i, id))}
              onAnswer={(text) => setAnswers((prev) => setAt(prev, i, text))}
            />
          ))}
        </div>

        {/* ---- recovery step 2: the account's own questions, then the new password ---- */}
        {recovering && step === 2 ? (
          <div className="space-y-3">
            <p className="text-xs leading-snug text-zinc-400">
              Answers aren't case-sensitive. All three have to match.
            </p>
            {recoverQuestionIds.map((id, i) => (
              <Field key={id} label={SECURITY_QUESTIONS.find((q) => q.id === id)?.prompt ?? "Security question"}>
                <TextInput
                  autoFocus={i === 0}
                  value={recoverAnswers[i] ?? ""}
                  onChange={(e) => setRecoverAnswers((prev) => setAt(prev, i, e.target.value))}
                  autoComplete="off"
                  data-ph-mask
                />
              </Field>
            ))}
            <div>
              <Field label="New password">
                <TextInput
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              </Field>
              <FieldNote tone={password && password.length >= PASSWORD_MIN ? "ok" : undefined}>
                At least {PASSWORD_MIN} characters.
              </FieldNote>
            </div>
            <div>
              <Field label="Confirm new password">
                <TextInput
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  placeholder="••••••••"
                />
              </Field>
              <FieldNote tone={confirmTone}>
                {!confirm ? "" : password === confirm ? "Passwords match." : "Passwords don't match."}
              </FieldNote>
            </div>
          </div>
        ) : null}

        {/* The robot check. The component renders its checkbox row here — or, opened,
            the 3×3 grid, in which case everything else folds away until it settles
            (a grid AND the form would overflow a short phone). Mounted across the mode
            swap AND across the step change: unmounting it throws away a solved token
            and deals a fresh grid, which is why this is class-hidden and never
            conditionally rendered. */}
        {goonchaEnabled ? (
          <div className={cx("mt-3", ((registering && step === 1) || (recovering && step === 2)) && "hidden")}>
            <Gooncha
              mode={recovering ? "login" : mode}
              token={goonchaToken}
              onToken={setGoonchaToken}
              expanded={goonchaOpen}
              onExpanded={setGoonchaOpen}
            />
          </div>
        ) : null}

        <div
          className={cx(
            "mt-2 min-h-[1.1rem] text-xs",
            goonchaOpen && "hidden",
            error ? "text-red-400" : "text-transparent",
          )}
          aria-live="polite"
        >
          {error ?? "placeholder"}
        </div>

        {/* Conditionally RENDERED, not class-hidden: Button carries its own inline-flex,
            which outranks a `hidden` utility in the cascade. */}
        {!goonchaOpen ? (
          <div className="flex gap-2">
            {onStage2 ? (
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setStep(1);
                  setError(null);
                }}
              >
                Back
              </Button>
            ) : null}
            <Button type="submit" variant="primary" className="w-full" disabled={submitDisabled}>
              {submitLabel}
            </Button>
          </div>
        ) : null}

        {mode === "login" ? (
          <p className={cx("mt-3 text-center text-xs", goonchaOpen && "hidden")}>
            <button
              type="button"
              className="text-zinc-400 hover:text-zinc-200 hover:underline"
              onClick={() => {
                setMode("recover");
                setStep(1);
                setError(null);
              }}
            >
              Forgot your password?
            </button>
          </p>
        ) : null}

        <p className={cx("mt-4 text-center text-xs text-zinc-500", (goonchaOpen || onStage2) && "hidden")}>
          {recovering ? "Remembered it?" : mode === "login" ? "No account yet?" : "Already have an account?"}{" "}
          <button
            type="button"
            className="font-medium text-[var(--color-accent-soft)] hover:underline"
            onClick={() => {
              setMode(mode === "login" ? "register" : "login");
              setStep(1);
              setError(null);
            }}
          >
            {mode === "login" ? "Create one" : "Sign in"}
          </button>
        </p>
      </form>
    </div>,
    document.body,
  );
}
